"use client";

import React, { useState, useEffect, useRef } from "react";

import Link from "next/link";
import Image from "next/image";

import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { resolvePageLink } from "@/sanity/globals/CTA";
import { IFooter } from "@/sanity/lib/definitions";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import PlatformUserCount from "@/components/global/ui/PlatformUserCount";
import { resolveI18nText, resolveI18nRichtext, NON_DEFAULT_LANGUAGES, DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";

import icons from "@/public/footer/icons.png";
import arrowBtn from "@/public/footer/arrow-btn.svg";
import trademaniaLogo from "@/public/footer/trademania-logo.svg";

export default function FooterV2({
  data,
  type = "full",
  legalNotice = false,
  hideMenus = false,
  hideLogo = false,
  className,
}: {
  data: IFooter;
  type?: "full" | "lp";
  legalNotice?: boolean;
  hideMenus?: boolean;
  hideLogo?: boolean;
  className?: string;
}) {
  const pathname = usePathname();

  const [currentTime, setCurrentTime] = useState<Date | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setCurrentTime(new Date());

    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const isMobile = typeof window !== "undefined" && window.innerWidth < 993;
  const prefersReduced = useReducedMotion();
  const layeredRef = useRef<HTMLDivElement | null>(null);
  const inView = useInView(layeredRef, { amount: 0.3 });

  const language =
    NON_DEFAULT_LANGUAGES.find((l) => pathname.startsWith(`/${l}/`) || pathname === `/${l}`) ?? DEFAULT_LANGUAGE;
  if (!data) return null;

  const vBack = {
    collapsed: { y: "0rem" },
    expanded: { y: isMobile ? "-3rem" : "-14rem" },
  };
  const vMid = {
    collapsed: { y: "0rem" },
    expanded: { y: isMobile ? "-1.5rem" : "-7rem" },
  };

  const transition = prefersReduced ? { duration: 0 } : { duration: 0.6, ease: "easeOut" };
  const state = inView ? "expanded" : "collapsed";

  return (
    <footer className={cn("w-full relative z-10 bg-[#0A1A1D]", className)}>
      <div
        className={cn(
          "w-[min(230rem,100%)] px-[2rem]  mx-auto flex flex-col",
          type === "full" ? "pt-[3rem] lg:pt-[7rem] pb-[2rem]" : "pb-[3rem]",
        )}>
        {type === "full" && !hideMenus && (
          <div className="flex flex-col justify-between items-center w-full lg:flex-row-reverse lg:items-start gap-[4rem] pb-[7rem] lg:pb-[17rem]">
            {/* menus */}
            <div className="grid grid-cols-2 justify-center items-start md:flex md:justify-between gap-x-[5rem] gap-y-[4rem] lg:gap-x-[8rem]">
              {data?.menu &&
                data?.menu?.length &&
                data?.menu?.map((menuItem, index) => (
                  <div
                    key={index}
                    className="flex flex-col justify-center items-start gap-[1rem] lg:min-w-[31.625rem]">
                    <p className="mb-0 font-geistmono text-white/20 text-[1.75rem]">
                      {resolveI18nText(menuItem.title, menuItem.titleI18n, language)}
                    </p>
                    <ul className="flex flex-col items-start gap-[1.5rem]">
                      {menuItem.items != null &&
                        menuItem?.items?.length > 0 &&
                        menuItem?.items?.map((item, index) => {
                          const href = resolvePageLink(item?.link, language) ?? "/";
                          const isActive = pathname === href;

                          return (
                            <li key={index + 100}>
                              <Link
                                href={resolvePageLink(item?.link, language) ?? "/"}
                                target={item?.link?.openInNewTab ? "_blank" : "_self"}
                                className={cn(
                                  "group w-full flex justify-between items-center gap-[1rem] relative",
                                  isActive && "pointer-events-none",
                                )}>
                                {/* underline hover */}
                                <span
                                  className={cn(
                                    "absolute bottom-0 left-0 w-0 h-[0.125rem] bg-[#D9F9B9] duration-300 transition-all ease-in-out group-hover:w-full",
                                    isActive && "w-full",
                                  )}
                                />
                                {/* end underline hover */}
                                <span
                                  className={cn(
                                    "text-white/60 text-[2.25rem] duration-300 transition-all ease-in-out group-hover:text-[#D9F9B9] group-hover:translate-x-2",
                                    isActive && "text-[#D9F9B9] translate-x-2",
                                  )}>
                                  {resolveI18nText(item.title, item.titleI18n, language)}
                                </span>
                                <Image
                                  className={cn(
                                    "min-w-[2rem] min-h-[2rem] opacity-0 -rotate-45 transition-all duration-300 ease-in-out group-hover:opacity-100 group-hover:rotate-0",
                                    isActive && "opacity-100 rotate-0",
                                  )}
                                  src={arrowBtn}
                                  alt="arrow"
                                />
                              </Link>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                ))}
            </div>
            {/* end menus */}

            {/* join community box */}
            <div className="p-[2rem] bg-[#112628] flex flex-col gap-[2rem] w-full sm:w-[min(48.875rem,100%)] flex-shrink-0">
              <div className="flex flex-col items-start">
                <p className="font-medium text-white mb-[.75rem] font-funnel text-[2.25rem]">Trademania</p>

                <PlatformUserCount
                  displayMode="online/offline"
                  className="mb-[1rem]"
                />

                <p className="mb-0 leading-[1.3] text-white/60 text-[2rem]">Est. Jul 2021</p>
              </div>

              {resolveI18nRichtext(data.communityText, data.communityTextI18n, language) && (
                <div className="richtext richtext-start prose-p:text-[2rem] prose-p:text-white/60">
                  <PortableText
                    value={resolveI18nRichtext(data.communityText, data.communityTextI18n, language)!}
                    components={richTextComponent}
                  />
                </div>
              )}

              <Image
                className="!w-full"
                src={icons}
                quality={100}
                alt="footer icons"
              />

              <a
                className="flex justify-center items-center border transition-opacity hover:opacity-80 text-[#0A1A1D] border-white/10 text-[2.25rem] font-[500] py-[1.5rem] rounded-[1rem] bg-[#D9F9B9]"
                href={data?.communityLink}
                target={"_blank"}>
                <span>{resolveI18nText(data?.communityTitle, data?.communityTitleI18n, language)}</span>
              </a>
            </div>
            {/* end join community box */}
          </div>
        )}

        <hr className="h-[0.125rem] border-white/10" />

        <div
          className={cn(
            "w-full pt-[3rem] flex flex-col lg:flex-row justify-between items-center gap-[2rem]",
            type === "full" && (hideLogo ? "mb-[2rem] lg:mb-0" : "mb-[8rem] lg:mb-[17.5rem]"),
          )}>
          <div className="flex justify-center items-center lg:order-2 lg:pb-0 gap-[3rem] pb-[1rem] lg:gap-[4rem]">
            {data?.impressumLink && (
              <Link
                className="text-white uppercase opacity-70 transition-opacity hover:underline hover:opacity-90 font-geistmono text-[2rem] font-[400]"
                href={resolvePageLink(data?.impressumLink, language) ?? "/"}
                target={data.impressumLink?.openInNewTab ? "_blank" : "_self"}>
                <span>{resolveI18nText(data?.impressumTitle, data?.impressumTitleI18n, language)}</span>
              </Link>
            )}

            {data?.datenschutzLink && (
              <Link
                className="text-white uppercase opacity-70 transition-opacity hover:underline hover:opacity-90 font-geistmono text-[2rem] font-[400]"
                href={resolvePageLink(data?.datenschutzLink, language) ?? "/"}
                target={data.datenschutzLink?.openInNewTab ? "_blank" : "_self"}>
                <span>{resolveI18nText(data?.datenschutzTitle, data?.datenschutzTitleI18n, language)}</span>
              </Link>
            )}
          </div>

          <p className="mb-0 leading-none text-white uppercase opacity-70 lg:order-3 font-geistmono text-[2rem] font-[400] lg:text-end lg:w-[50rem]">
            {mounted && currentTime
              ? currentTime.toLocaleDateString(language === "en" ? "en-US" : "de-DE", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                })
              : ""}
          </p>

          <p className="mb-0 leading-none text-center text-white opacity-70 lg:order-1 font-geistmono text-[2rem] font-[400] lg:text-start lg:w-[50rem]">
            © {new Date().getFullYear()} Trademania LLC. <br /> All rights reserved.
          </p>
        </div>

        {type === "full" && !hideLogo && (
          <div
            ref={layeredRef}
            className="relative">
            <motion.div
              className="absolute top-0 left-0 w-full h-full bg-[#0A1A1D] pt-[.5rem] lg:pt-[1.5rem]"
              variants={vBack}
              initial="collapsed"
              animate={state}
              transition={transition}>
              <Image
                className="!w-full opacity-10"
                src={trademaniaLogo}
                alt="BitBull Logo"
              />
            </motion.div>
            <motion.div
              className="absolute top-0 left-0 z-10 w-full h-full bg-[#0A1A1D] pt-[.5rem] lg:pt-[1.5rem]"
              variants={vMid}
              initial="collapsed"
              animate={state}
              transition={transition}>
              <Image
                className="!w-full opacity-30"
                src={trademaniaLogo}
                alt="BitBull Logo"
              />
            </motion.div>
            <div className="relative z-20 bg-[#0A1A1D] pt-[.5rem] lg:pt-[1.5rem]">
              <Image
                className="!w-full"
                src={trademaniaLogo}
                alt="BitBull Logo"
              />
            </div>
          </div>
        )}

        {legalNotice && resolveI18nRichtext(data.legalNotice, data.legalNoticeI18n, language) && (
          <div
            className={`richtext richtext-center ${hideLogo ? "mt-[2rem] lg:mt-[4rem] " : "mt-[5rem] lg:mt-[7rem]"} prose-p:text-[1.75rem] prose-p:text-white/40`}>
            <PortableText
              value={resolveI18nRichtext(data.legalNotice, data.legalNoticeI18n, language)!}
              components={richTextComponent}
            />
          </div>
        )}
      </div>
    </footer>
  );
}
